import type { ScopedCacheManager, ManagedCache, CacheOptions, CacheStats } from '@gzl10/nexus-sdk'
import type { CacheManager, CacheLogger } from './cache-manager.js'

export class ScopedCacheManagerImpl implements ScopedCacheManager {
  private localNames = new Set<string>()

  constructor(
    private scope: string,
    private manager: CacheManager,
    private maxCaches: number,
    private logger?: CacheLogger
  ) {}

  create<T = unknown>(name: string, options?: CacheOptions): ManagedCache<T> {
    if (this.localNames.has(name)) {
      throw new Error(`Cache "${this.scope}:${name}" already exists. Use getOrCreate() for idempotent access.`)
    }
    return this.createInternal<T>(name, options)
  }

  getOrCreate<T = unknown>(name: string, options?: CacheOptions): ManagedCache<T> {
    const existing = this.manager.get<T>(this.fullName(name))
    if (existing) return existing
    return this.createInternal<T>(name, options)
  }

  get<T = unknown>(name: string): ManagedCache<T> | undefined {
    return this.manager.get<T>(this.fullName(name))
  }

  async clearAll(): Promise<void> {
    for (const name of this.localNames) {
      await this.manager.get(this.fullName(name))?.clear()
    }
  }

  stats(): Record<string, CacheStats> {
    const result: Record<string, CacheStats> = {}
    for (const name of this.localNames) {
      const cache = this.manager.get(this.fullName(name))
      if (cache) result[name] = cache.getStats()
    }
    return result
  }

  async destroy(): Promise<void> {
    for (const name of this.localNames) {
      await this.manager.unregister(this.fullName(name))
    }
    this.localNames.clear()
  }

  private createInternal<T>(name: string, options?: CacheOptions): ManagedCache<T> {
    if (this.localNames.size >= this.maxCaches) {
      this.logger?.warn({ scope: this.scope, max: this.maxCaches }, 'Scope cache limit reached')
      throw new Error(`Scope "${this.scope}" exceeded max caches (${this.maxCaches})`)
    }

    const full = this.fullName(name)
    const cache = this.manager.createCache<T>(
      full,
      options,
      (events) => this.manager.addRulesForCache(full, cache, events)
    )

    this.localNames.add(name)
    this.manager.register(full, this.scope, cache, options?.invalidateOn)
    return cache
  }

  private fullName(name: string): string {
    return `${this.scope}:${name}`
  }
}
